import React, { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { useUserContext } from '../../contexts/userContext';
import { Alert, Button, Checkbox, FormControlLabel, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from 'dayjs';
import { useConfirm } from 'material-ui-confirm';

import { IFuzzySearchResult } from '../../types/system';
import { IVendorCustomer, IVendorDetails, IVendorLoadMinimums } from '../../types/vendor';

import { AppConfig } from '../../config/appConfig';
import { ErrorProvider } from '../../classes/errorProvider';
import { useListContext } from '../../contexts/listContext';
import { UtilityService } from '../../services/utilityService';
import { VendorService } from '../../services/vendorService';

import NumberField from '../shared/numberField';
import { Icons } from '../icons';

import '../../styles/vendorDetailsForm.scss';
import Json from '../json';

export interface IVendorDetailsForm {
	action: "add" | "edit";
	id?: string;
	customers?: IVendorCustomer[];
	details: IVendorDetails;
	reloadData: () => void;
}

const VendorDetailsForm: React.FunctionComponent<IVendorDetailsForm> = ({ action, id, customers, details, reloadData }) => {
	const confirm = useConfirm();
	const navigate = useNavigate();
	const userContext = useUserContext();
	const listContext = useListContext();
	const [formFields, setFormFields] = useState<IVendorDetails>(details);
	const [originalVendorName, setOriginalVendorName] = useState<string>(details?.name);
	const [loadMiniumFormFields, setLoadMinimumFormFields] = useState<IVendorLoadMinimums>(details.loadMinimums || {});
	const [isSaving, setIsSaving] = useState(false);
	const [isCheckingNameForDupes, setIsCheckingNameForDupes] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | JSX.Element | undefined>();
	const [successMessage, setSuccessMessage] = useState<string | undefined>();
	

	const handleLastAuditDateChange = (value: Dayjs | null) => {
		if (value) {
			let newFields = formFields;
			newFields.lastAuditDate = value.toDate();
			setFormFields(newFields);
		}
	}

	const doSave = async () => {
		clearSaveResult();
		setIsSaving(true);

		const combinedFormFields = { ...formFields, ['loadMinimums']: loadMiniumFormFields };

		if (action == "add") {
			VendorService.addVendor(combinedFormFields)
				.then((response) => {
					console.log('VendorService.addVendor: SUCCESS', response);
					setSuccessMessage("Vendor has been added.");
					setTimeout(() => {
						navigate("/vendor/" + response.data.id);
					}, AppConfig.addSuccessToastMs);
				})
				.catch((error) => {
					console.log('VendorService.addVendor: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
					window.scrollTo(0, 0);
				});
		}
		else if (action == "edit" && id) {
			VendorService.setDetails(id, combinedFormFields)
				.then((response) => {
					console.log('VendorService.setDetails: SUCCESS', response);

					setLoadMinimumFormFields(response.data.details?.loadMinimums || {});
					reloadData();
					setSuccessMessage("Vendor details have been saved.");
					setTimeout(() => {
						setSuccessMessage(undefined);
					}, AppConfig.editSuccessToastMs);
				})
				.catch((error) => {
					console.log('VendorService.setDetails: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
					window.scrollTo(0, 0);
				});
		}
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		// Fuzzy check existing vendor names for potential duplicates before saving		
		event.preventDefault();
		setErrorMessage(undefined);

		// If the name wasn't changed then don't bother checking for duplicates
		if (action == "edit" && formFields?.name == originalVendorName) {
			await doSave();
			return;
		}

		setIsCheckingNameForDupes(true);

		VendorService.fuzzySearchForDuplicateVendorNames({ name: formFields?.name, excludeId: action == "edit" ? +id : undefined })
			.then(async (response) => {
				console.log('VendorService.fuzzySearchForDuplicateVendorNames: SUCCESS', response);
				const matches: IFuzzySearchResult[] = response.data;
				if (matches.length > 0) {
					if (await UtilityService.ConfirmationModal(
						confirm,
						<div className="dupes-dialog-title">
							<Icons.Warning fill="#272f50" height="14px"></Icons.Warning>
							Potential Duplicates
						</div>,
						<div className="dupes-dialog-content">
							<strong>The name {formFields.name}</strong> has a similar name to <strong>{matches.length}</strong> existing vendor{matches.length > 1 ? "s" : ""}. Please review the conflicts below before proceeding.<br />
							<ul>
								{matches.map((item, index) => (
									<li key={index}>
										<a href={`/vendor/${item.id}`} target="_blank" rel="noopener noreferrer">
											<Icons.ViewItem height="14px"></Icons.ViewItem>
											{item.name}
										</a>
									</li>
								))}
							</ul>
						</div>,
						{
							confirmationText: "Continue",
							confirmationButtonProps: { color: 'primary', variant: 'contained' },
							cancellationText: "Cancel"
						}
					) == false) {
						return;
					}
				}

				await doSave();
			})
			.catch((error) => {
				console.log('VendorService.fuzzySearchForDuplicateVendorNames: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsCheckingNameForDupes(false);
			});
	}

	const handleUsesExtrenalPriceIdsCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!id) {
			setFormFields({ ...formFields, ['usesExternalPriceIds']: event.target.checked });
		}
	};

	const clearSaveResult = () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
	};

	return (
		<section className="hwi-vendor-details-form">
			{successMessage &&
				<Alert className="alert-success"
					severity="success"
					action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
					{successMessage}
				</Alert>
			}
			{errorMessage &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
					{errorMessage}
				</Alert>
			}
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Form onSubmit={(event) => handleSubmit(event)}>
					<div className="row">
						<InputLabel>Name *</InputLabel>
						<TextField className="name-field" required name="name" value={formFields.name} autoFocus={action == "add"} autoComplete="off" size="small" onChange={(event) => { setFormFields({ ...formFields, ["name"]: event.target.value }) }}></TextField>
					</div>
					<div className="row">
						<InputLabel>Status *</InputLabel>
						<Select
							value={formFields.status}
							className="status-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["status"]: event.target.value }) }}
						>
							{listContext.vendorStatuses.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>Last Audit Date</InputLabel>
						<DatePicker className="last-audit-date-field" value={dayjs(formFields.lastAuditDate)} onChange={(value) => handleLastAuditDateChange(value)} />
					</div>
					<div className="row">
						<InputLabel>Method</InputLabel>
						<Select
							value={formFields.method}
							className="method-field"
							onChange={(event) => { setFormFields({ ...formFields, ["method"]: event.target.value }) }}
						>
							{listContext.vendorMethods.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>EPA ID</InputLabel>
						<TextField className="epa-id-field" name="epaId" inputProps={{ maxLength: 15 }} value={formFields.epaId} size="small" onChange={(event) => { setFormFields({ ...formFields, ["epaId"]: event.target.value }) }}></TextField>
					</div>
					{id ? (
						<>
							{formFields.usesExternalPriceIds &&
								<div className='row'>
									<div className='uses-external-price-ids-message'>&bull;&nbsp;This vendor uses external price IDs.</div>
								</div>
							}
						</>
					) : (
						<div className="row">
							<FormControlLabel className='uses-external-price-ids-field' control={<Checkbox name="usesExternalPriceIds" checked={formFields.usesExternalPriceIds} onChange={handleUsesExtrenalPriceIdsCheck}></Checkbox>} label="Uses External Price IDs" />
						</div>
					)}
					{userContext?.permissions?.canChangeShipmentDatesForVendorsRequiringEscalatedPermission &&
						<div className="row">
							<FormControlLabel className='requires-escalated-date-change-permission-field' control={<Checkbox name="requiresEscalatedDateChangePermission" checked={formFields.requiresEscalatedDateChangePermission} onChange={(event) => { setFormFields({ ...formFields, ["requiresEscalatedDateChangePermission"]: event.target.checked }) }}></Checkbox>} label="Requires Escalated Date Change Permission" />
						</div>
					}
					<div className="row">
						<FormControlLabel className='zero-weight-load-notifications-field' control={<Checkbox name="zeroWeightLoadNotifications" checked={formFields.zeroWeightLoadNotifications} onChange={(event) => { setFormFields({ ...formFields, ["zeroWeightLoadNotifications"]: event.target.checked }) }}></Checkbox>} label="Notify if load weight is zero" />
					</div>
					<div className='row no-bottom-margin'>
						<InputLabel className="load-minimums-header">Load Minimums by Trailer Type</InputLabel>
					</div>
					<div className="row">
						<InputLabel>Compactor</InputLabel>
						<div className='load-minimums-input-row'>
							<TextField className="compactor-load-minimum" name="comnpactor" value={loadMiniumFormFields?.compactor ?? ''} size="small" placeholder="No minimum weight set" onChange={(event) => { setLoadMinimumFormFields({ ...loadMiniumFormFields, ["compactor"]: parseInt(event.target.value) || undefined }) }}></TextField>
							{!!loadMiniumFormFields?.compactor && " lbs"}
						</div>
					</div>
					<div className="row">
						<InputLabel>Dry Van</InputLabel>
						<div className='load-minimums-input-row'>
							<TextField className="dry-van-load-minimum" name="dry-van" value={loadMiniumFormFields?.dryVan ?? ''} size="small" placeholder="No minimum weight set" onChange={(event) => { setLoadMinimumFormFields({ ...loadMiniumFormFields, ["dryVan"]: parseInt(event.target.value) || undefined }) }}></TextField>
							{!!loadMiniumFormFields?.dryVan && " lbs"}
						</div>
					</div>
					<div className="row">
						<InputLabel>Flat Bed</InputLabel>
						<div className='load-minimums-input-row'>
							<TextField className="flat-bed-load-minimum" name="flat-bed" value={loadMiniumFormFields?.flatBed ?? ''} size="small" placeholder="No minimum weight set" onChange={(event) => { setLoadMinimumFormFields({ ...loadMiniumFormFields, ["flatBed"]: parseInt(event.target.value) || undefined }) }}></TextField>
							{!!loadMiniumFormFields?.flatBed && " lbs"}
						</div>
					</div>
					<div className="row">
						<InputLabel>Lugger</InputLabel>
						<div className='load-minimums-input-row'>
							<TextField className="lugger-load-minimum" name="lugger" value={loadMiniumFormFields?.lugger ?? ''} size="small" placeholder="No minimum weight set" onChange={(event) => { setLoadMinimumFormFields({ ...loadMiniumFormFields, ["lugger"]: parseInt(event.target.value) || undefined }) }}></TextField>
							{!!loadMiniumFormFields?.lugger && " lbs"}
						</div>
					</div>
					<div className="row">
						<InputLabel>Open Top</InputLabel>
						<div className='load-minimums-input-row'>
							<TextField className="open-top-load-minimum" name="open-top" value={loadMiniumFormFields?.openTop ?? ''} size="small" placeholder="No minimum weight set" onChange={(event) => { setLoadMinimumFormFields({ ...loadMiniumFormFields, ["openTop"]: parseInt(event.target.value) || undefined }) }}></TextField>
							{!!loadMiniumFormFields?.openTop && " lbs"}
						</div>
					</div>
					<div className="row">
						<InputLabel>Roll Off</InputLabel>
						<div className='load-minimums-input-row'>
							<TextField className="roll-off-load-minimum" name="roll-off" value={loadMiniumFormFields?.rollOff ?? ''} size="small" placeholder="No minimum weight set" onChange={(event) => { setLoadMinimumFormFields({ ...loadMiniumFormFields, ["rollOff"]: parseInt(event.target.value) || undefined }) }}></TextField>
							{!!loadMiniumFormFields?.rollOff && " lbs"}
						</div>
					</div>
					<div className="row">
						<InputLabel>Tanker</InputLabel>
						<div className='load-minimums-input-row'>
							<TextField className="tanker-load-minimum" name="tanker" value={loadMiniumFormFields?.tanker ?? ''} size="small" placeholder="No minimum weight set" onChange={(event) => { setLoadMinimumFormFields({ ...loadMiniumFormFields, ["tanker"]: parseInt(event.target.value) || undefined }) }}></TextField>
							{!!loadMiniumFormFields?.tanker && " lbs"}
						</div>
					</div>
					<div className="row controls">
						<Button type="submit" className="save-button" variant="contained" disabled={isCheckingNameForDupes || isSaving}>{isCheckingNameForDupes || isSaving ? "Saving..." : "Save Details"}</Button>
					</div>
				</Form>
			</LocalizationProvider>
		</section>
	)
}

export default VendorDetailsForm;